/**
 * ── A HORA MARCADA DO CURSO ─────────────────────────────────────────────────
 *
 * Ordem do dono de 24/09/2026: um curso pode ter data de lançamento. A migração
 * `0428` explica por que uma coluna basta e por que o modo é o próprio status;
 * aqui mora o que se decide COM ela, e a frase que o aluno lê.
 *
 * ## Por que função pura, como as duas trancas vizinhas
 *
 * Pelo mesmo motivo de `tranca-por-pre-requisito.ts` e `tranca-por-avaliacao.ts`,
 * e com os mesmos leitores: a vitrine desenha o cartão, a página do curso
 * desenha o aviso, `exigirCurso` recusa a entrada e `exigirAula` recusa o corpo.
 * Quatro leitores e uma regra — escrita em SQL, seria copiada nas quatro com
 * pequenas diferenças que só apareceriam no dia em que alguém abrisse o curso
 * pelo link direto, que é precisamente o dia em que a diferença custa.
 *
 * ## Esta é a TERCEIRA razão de um cartão estar trancado, e não se parece com as outras
 *
 * «Você não comprou» leva à oferta. «Depende de outro curso» nomeia o que falta
 * e tem régua a preencher. Esta **não depende de nada que a pessoa faça**: não há
 * gesto que a adiante e não há nada a cumprir. Por isso a frase não nomeia
 * ninguém e não tem número de progresso — o que ela deve é a data, e só.
 *
 * Daí também não reusar o desenho da régua: mostrar «você está em 0%» sobre uma
 * coisa que não se cumpre estudando seria culpar a pessoa pelo calendário.
 */

import { tradutorDe, type Tradutor } from '../i18n/tela.js';

/** O que o curso precisa de trazer para esta régua. Nada além disto. */
export interface CursoComHoraMarcada {
  status: string;
  /** Nulo = sem hora marcada, que é o caso de quase todo curso. */
  availableAt: Date | null;
}

/**
 * O curso está à espera da hora dele?
 *
 * Só para o PUBLICADO: um rascunho com hora marcada está à espera de outra
 * coisa — de a varredura o publicar —, e do lado do aluno ele nem existe. Se
 * esta função dissesse «sim» para ele, a vitrine teria de o esconder por uma
 * segunda regra, e são as duas regras a decidir a mesma coisa que divergem.
 */
export function cursoEsperaAHora(curso: CursoComHoraMarcada, agora: Date): boolean {
  if (curso.status !== 'published') return false;
  if (curso.availableAt === null) return false;
  return curso.availableAt.getTime() > agora.getTime();
}

/**
 * O rascunho já passou da hora e tem de ser publicado?
 *
 * É o predicado da varredura, e ele vive aqui — e não num `where` do worker —
 * para que o teste da régua e o que corre em produção sejam a mesma frase.
 */
export function rascunhoJaVenceu(curso: CursoComHoraMarcada, agora: Date): boolean {
  if (curso.status !== 'draft') return false;
  if (curso.availableAt === null) return false;
  return curso.availableAt.getTime() <= agora.getTime();
}

const MES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

/** Meia-noite do dia daquele instante, para contar DIAS e não horas. */
function diaDe(d: Date): number {
  return Math.floor(new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / 86_400_000);
}

/**
 * A frase da espera — **uma só, para os quatro leitores**.
 *
 * ## Por que ela conta DIAS DE CALENDÁRIO, e não horas divididas por 24
 *
 * Porque «amanhã» é uma palavra sobre o calendário, não sobre 24 horas. Às 23h
 * de segunda, um curso que abre às 8h de terça abre **amanhã** — e faltam nove
 * horas, que dividido por 24 dá zero dias e produziria «abre hoje», que é falso
 * e soa a defeito. A conta é entre as meias-noites dos dois dias.
 *
 * ## O FUSO é o de quem executa esta função, e isso decide onde ela corre
 *
 * `getHours()` e `getDate()` falam do relógio do PROCESSO. No servidor isso é
 * UTC, e perto da meia-noite a frase erraria o dia para quem lê noutro fuso — o
 * mesmo defeito que o gotejamento da aula já tem documentado.
 *
 * Por isso a `frase` que viaja no payload é **reserva**, para quem não pode
 * derivar (o 403, o telemóvel), e a ponta que consegue importar esta função
 * deve chamá-la com o `Date` do relógio de quem lê. É a mesma arquitetura de
 * `frase-da-tranca-de-ordem.ts`: a REDAÇÃO é uma só e mora aqui; quem a executa
 * é quem sabe que horas são para aquela pessoa.
 *
 * ## Por que não há contagem ao segundo
 *
 * Porque um relógio a correr num cartão de vitrine é peso e movimento por uma
 * precisão que ninguém usa — e, no caso do rascunho, é precisão que o servidor
 * **não tem**: quem o publica é uma varredura de cinco em cinco minutos. A
 * frase promete o que é verdade e nada mais.
 */
export function fraseDaHoraMarcada(
  quando: Date,
  agora: Date,
  t: Tradutor = tradutorDe(null),
): string {
  const dias = diaDe(quando) - diaDe(agora);

  if (dias <= 0) {
    /* Hoje, e já passou da hora: quem chama não devia ter perguntado — mas
       responder «abriu» é melhor do que responder uma hora que já foi. */
    if (quando.getTime() <= agora.getTime()) return t('lancamento.jaAbriu');
    const h = quando.getHours();
    const m = quando.getMinutes();
    return m === 0
      ? t('lancamento.abreHojeAs', { hora: h })
      : t('lancamento.abreHojeAsMin', { hora: h, minuto: String(m).padStart(2, '0') });
  }
  if (dias === 1) return t('lancamento.abreAmanha');
  /* Até uma semana, o número de dias diz mais do que a data: «faltam 5 dias» é
     uma distância; «12 de março» é um compromisso. Passada a semana, inverte-se
     — ninguém conta 43 dias, e a data é que se marca na agenda. */
  if (dias <= 7) return t('lancamento.abreEmDias', { dias });
  /*
   * O NOME do mês continua a sair de `MES`, em português, e isto é uma
   * dívida NOMEADA: traduzir os doze meses é mais um lote de chaves, e a
   * cerca da varredura mantém-no à vista em vez de o deixar esquecido.
   */
  return t('lancamento.abreEmData', { dia: quando.getDate(), mes: MES[quando.getMonth()] ?? '' });
}

/** O que viaja para as telas quando o curso espera a hora. */
export interface LancamentoDoCurso {
  /** ISO 8601, com fuso. A contagem é FORMA e sai daqui, na tela de quem lê. */
  em: string;
  /** Pronta, do core. Quem desenha não redige — a lei de sempre. */
  frase: string;
}

/** O objeto do curso à espera, ou nada. Um leitor só, para os quatro. */
export function lancamentoDoCurso(
  curso: CursoComHoraMarcada,
  agora: Date,
): LancamentoDoCurso | null {
  if (!cursoEsperaAHora(curso, agora)) return null;
  const quando = curso.availableAt!;
  return { em: quando.toISOString(), frase: fraseDaHoraMarcada(quando, agora) };
}

/**
 * ── A VARREDURA QUE PUBLICA: QUEM ELA RECLAMA ──────────────────────────────
 *
 * O worker lê os candidatos (rascunho com hora marcada) e pergunta a esta
 * função quais já venceram. **A decisão não é escrita em SQL**, e é de
 * propósito: o `where` do banco e a régua do teste seriam duas frases sobre a
 * mesma coisa, e é assim que uma passa a dizer `<` onde a outra diz `<=`.
 *
 * O `update` continua a levar `status = 'draft'` no `where` dele — não para
 * decidir, mas para não sobrepor o tutor que publicou à mão entre a leitura e a
 * escrita.
 */
export function rascunhosQueVenceram<T extends CursoComHoraMarcada & { id: string }>(
  candidatos: readonly T[],
  agora: Date,
): T[] {
  return candidatos.filter((c) => rascunhoJaVenceu(c, agora));
}

// ─────────────────────────────────────────────────── o aviso da estreia

/**
 * ── O CURSO ESTREOU: A JANELA E A CHAVE ────────────────────────────────────
 *
 * Ordem do dono de 24/09/2026, quando lhe contei que o aluno receberia «3 aulas
 * novas» em vez de «o curso estreou»: *«sim, quero»*.
 *
 * ## Por que uma janela, e não «todo curso já aberto»
 *
 * Sem janela, **todo curso que já estreou seria candidato em cada passada**, e a
 * varredura passaria a vida a perguntar ao banco por lançamentos de meses atrás
 * para a chave de dedup os recusar um a um. A janela é o corte grosso; a chave
 * é quem garante a vez única.
 *
 * Doze horas, e não os quinze minutos do «a live começou»: uma live perdida não
 * se recupera, mas uma estreia anunciada com algumas horas de atraso ainda é uma
 * estreia — e a varredura pode ter estado parada. Passadas doze horas a notícia
 * envelheceu, e mandá-la seria pior do que não a mandar.
 *
 * ## Por que a chave leva a HORA, e não só o curso
 *
 * `curso_estreou:<id>:<hora>` — como a do evento. Se o tutor voltar atrás e
 * remarcar a estreia para outro dia, aquilo é uma estreia nova e merece aviso
 * novo; com a chave só do curso, o segundo lançamento sairia mudo. E se ele
 * remarcar para a MESMA hora, a chave é a mesma e ninguém recebe duas vezes.
 */
export const JANELA_DA_ESTREIA_MS = 12 * 60 * 60 * 1000;

/** O curso acabou de abrir — dentro da janela em que a notícia ainda é notícia. */
export function cursoEstreouAgora(quando: Date, agora: Date): boolean {
  const d = agora.getTime() - quando.getTime();
  return d >= 0 && d <= JANELA_DA_ESTREIA_MS;
}

/** A chave de dedup. Leva a hora: remarcar para outro dia é uma estreia nova. */
export function chaveDaEstreia(courseId: string, quando: Date): string {
  return `curso_estreou:${courseId}:${quando.getTime()}`;
}

// ──────────────────────────────────────────────── a ementa da carta da estreia

/** Uma linha crua da consulta da ementa: um módulo do curso e o que ele tem. */
export interface ModuloDoCurso {
  id: string;
  /** Nulo = módulo de raiz. Não-nulo = submódulo, e as aulas dele sobem ao pai. */
  parentId: string | null;
  titulo: string;
  posicao: number;
  /** Quantas aulas PUBLICADAS estão neste módulo — sem contar as dos filhos. */
  aulas: number;
}

/**
 * ── A EMENTA: OS MÓDULOS DE RAIZ, COM AS AULAS DOS FILHOS SOMADAS ───────────
 *
 * Ordem do dono de 28/09/2026, depois de receber a carta da estreia sem nenhuma
 * indicação de tamanho: a ementa é **os módulos com a contagem de aulas**.
 *
 * ## Por que a dobra é aqui, em TypeScript, e não no `group by`
 *
 * Porque `course_module.parent_id` aponta para a própria tabela e a profundidade
 * não é garantida em coluna nenhuma. Em SQL isto pediria uma recursiva — e SQL
 * cru desta casa já mentiu com o typecheck verde (`least()`, `LATERAL`, arrays).
 * A consulta fica trivial («os módulos não escondidos e as aulas publicadas de
 * cada um») e a árvore dobra-se aqui, onde uma cerca pura a mede sem banco e sem
 * fixture, a qualquer profundidade.
 *
 * ## O submódulo não é uma linha da ementa
 *
 * Um curso com «Módulo 1 › Parte A, Parte B» tem UM módulo para quem lê, não
 * três. Imprimir os três diria «3 módulos» de um curso que o tutor desenhou com
 * um — e a contagem de aulas apareceria duas vezes, uma no pai e outra no filho.
 * As aulas do filho sobem ao pai, e o filho não aparece.
 *
 * ## O órfão conta como raiz
 *
 * Um `parent_id` que aponta para um módulo ESCONDIDO (ou que a consulta não
 * trouxe) não some: ele passa a valer como raiz. A direção do erro é a mesma de
 * `itensVisiveis` com o vínculo órfão — errar para o lado de MOSTRAR, porque um
 * defeito de junção a esconder conteúdo pago é pior do que uma linha a mais numa
 * carta.
 */
export function ementaDoCurso(modulos: readonly ModuloDoCurso[]): { titulo: string; aulas: number }[] {
  const porId = new Map(modulos.map((m) => [m.id, m]));

  /** A raiz deste módulo — sobe pelos pais, e para se o pai não veio. */
  function raizDe(m: ModuloDoCurso): ModuloDoCurso {
    const vistos = new Set<string>([m.id]);
    let atual = m;
    while (atual.parentId !== null) {
      const pai = porId.get(atual.parentId);
      /* Pai ausente (escondido, ou de outro curso) → este é a raiz. */
      if (!pai) return atual;
      /*
       * Ciclo em `parent_id` — a coluna é livre e uma árvore com laço penduraria
       * o worker no `while`, com nenhum aviso a sair.
       *
       * Devolve `m`, o módulo de PARTIDA, e não `atual`: com `atual` nenhum
       * membro do ciclo seria raiz de si mesmo (A aponta B, B aponta A), o filtro
       * lá abaixo descartava os dois e a ementa saía **vazia** — a carta calava o
       * tamanho do curso sem ninguém saber porquê. Foi a cerca do ciclo que o
       * apanhou, nesta função, no dia em que ela nasceu. Cada membro do laço
       * passa a ser a sua própria raiz: numa árvore corrompida a ementa fica
       * estranha, e nunca perde conteúdo.
       */
      if (vistos.has(pai.id)) return m;
      vistos.add(pai.id);
      atual = pai;
    }
    return atual;
  }

  const soma = new Map<string, number>();
  for (const m of modulos) {
    const raiz = raizDe(m);
    soma.set(raiz.id, (soma.get(raiz.id) ?? 0) + m.aulas);
  }

  return modulos
    .filter((m) => raizDe(m).id === m.id)
    .sort((a, b) => a.posicao - b.posicao)
    .map((m) => ({ titulo: m.titulo, aulas: soma.get(m.id) ?? 0 }));
}
